import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import type { AdminVehicle, AssetClass, PoolDevice } from '@/lib/types';

/** The pool, and the two directions a device can move. */

export interface PoolFilters {
  state?: 'free' | 'allocated';
  search?: string;
  organizationId?: string;
}

export interface AllocateInput {
  deviceId: string;
  organizationId: string;
  name: string;
  plateNumber: string;
  assetClass?: AssetClass;
  groupId?: string | null;
}

export function usePartnerDevices(partnerId: string, filters: PoolFilters = {}) {
  const params = new URLSearchParams();
  if (filters.state) params.set('state', filters.state);
  if (filters.search) params.set('search', filters.search);
  if (filters.organizationId) params.set('organizationId', filters.organizationId);

  const query = params.toString();

  return useQuery({
    queryKey: ['partners', partnerId, 'devices', filters],
    queryFn: () =>
      apiFetch<{ data: PoolDevice[] }>(
        `/v1/admin/partners/${partnerId}/devices${query ? `?${query}` : ''}`
      ),
    enabled: Boolean(partnerId),
  });
}

/** An allocation changes the pool, the partner's counts and the org's fleet. */
function useAllocationInvalidation(partnerId: string) {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ['partners'] });
    queryClient.invalidateQueries({ queryKey: ['organizations'] });
    queryClient.invalidateQueries({ queryKey: ['partners', partnerId, 'devices'] });
  };
}

export function useAllocateDevice(partnerId: string) {
  const invalidate = useAllocationInvalidation(partnerId);

  return useMutation({
    mutationFn: ({ deviceId, ...body }: AllocateInput) =>
      apiFetch<{ device: PoolDevice; vehicle: AdminVehicle }>(
        `/v1/admin/partners/${partnerId}/devices/${deviceId}/allocate`,
        { method: 'POST', body: JSON.stringify(body) }
      ),
    onSuccess: invalidate,
  });
}

export function useDeallocateDevice(partnerId: string) {
  const invalidate = useAllocationInvalidation(partnerId);

  return useMutation({
    mutationFn: (deviceId: string) =>
      apiFetch<{ deviceId: string; vehicleId: string }>(
        `/v1/admin/partners/${partnerId}/devices/${deviceId}/deallocate`,
        { method: 'POST' }
      ),
    onSuccess: invalidate,
  });
}
